import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fetchChantingIndex } from "@/lib/chanting/chanting-service";
import {
  fetchTownhallAdminDetail,
  saveTownhallAdminDetail,
  uploadTownhallDeityImage,
  importTownhallSlokasFromUrl,
} from "@/lib/chanting/townhall-admin-api";

export function TownhallAdmin() {
  const [chants, setChants] = useState([]);
  const [chantId, setChantId] = useState("");
  const [description, setDescription] = useState("");
  const [deityImage, setDeityImage] = useState("");
  const [slidesJson, setSlidesJson] = useState("[]");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [importUrl, setImportUrl] = useState("");

  useEffect(() => {
    fetchChantingIndex()
      .then((data) => {
        const list = Array.isArray(data?.chants) ? data.chants : [];
        setChants(list);
        if (list.length && !chantId) setChantId(list[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!chantId) return;
    fetchTownhallAdminDetail(chantId)
      .then((data) => {
        setDescription(data?.override?.description || data?.detail?.description || "");
        setDeityImage(data?.override?.deity_image || data?.detail?.deity_image || "");
        setSlidesJson(JSON.stringify(data?.detail?.slides || data?.override?.slides || [], null, 2));
      })
      .catch((e) => setMessage(e.message || "Failed to load Townhall chant."));
  }, [chantId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Townhall Editor (D1)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Chant</label>
          <select
            value={chantId}
            onChange={(e) => setChantId(e.target.value)}
            className="h-9 w-full rounded border border-input bg-background px-3 text-sm"
          >
            {chants.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Import sloka from public URL</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              className="h-9 w-full rounded border border-input bg-background px-3 text-sm"
              placeholder="https://vignanam.org/hindi/sree-vishnu-sahasra-nama-stotram.html"
            />
            <Button
              type="button"
              variant="outline"
              disabled={!chantId || !importUrl.trim() || saving}
              onClick={async () => {
                setMessage("");
                try {
                  setSaving(true);
                  const imported = await importTownhallSlokasFromUrl(chantId, {
                    url: importUrl.trim(),
                    languages: ["en", "hi", "te", "ta", "kn", "ml", "gu", "bn", "or", "pa", "sa"],
                  });
                  const slides = imported?.detail?.slides || imported?.override?.slides || [];
                  setSlidesJson(JSON.stringify(slides, null, 2));
                  setMessage(
                    `Imported from URL (${imported?.imported?.source_scheme || "auto"}) and generated transliterations.`
                  );
                } catch (e) {
                  setMessage(e?.message || "Import failed. Source site may block automated fetch.");
                } finally {
                  setSaving(false);
                }
              }}
            >
              Import & auto-generate
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Imports the master sloka block and auto-generates transliterations for Indic scripts.
          </p>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Deity image URL/path</label>
          <input
            value={deityImage}
            onChange={(e) => setDeityImage(e.target.value)}
            className="h-9 w-full rounded border border-input bg-background px-3 text-sm"
            placeholder="/images/sacred-library/deities/hanuman.svg"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Upload deity image</label>
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file || !chantId) return;
              const reader = new FileReader();
              reader.onload = async () => {
                const raw = String(reader.result || "");
                try {
                  setSaving(true);
                  const res = await uploadTownhallDeityImage(chantId, {
                    data_base64: raw,
                    mime: file.type || "image/png",
                  });
                  setDeityImage(res?.detail?.deity_image || res?.image_url || deityImage);
                  setMessage("Deity image uploaded.");
                } catch (err) {
                  setMessage(err?.message || "Image upload failed.");
                } finally {
                  setSaving(false);
                }
              };
              reader.readAsDataURL(file);
            }}
            className="block w-full text-sm"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="min-h-[72px] w-full rounded border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Slokas JSON (Sanskrit + transliteration)</label>
          <textarea
            value={slidesJson}
            onChange={(e) => setSlidesJson(e.target.value)}
            className="min-h-[260px] w-full rounded border border-input bg-background px-3 py-2 text-xs font-mono"
          />
        </div>

        <Button
          disabled={!chantId || saving}
          onClick={async () => {
            setMessage("");
            try {
              setSaving(true);
              const slides = JSON.parse(slidesJson);
              await saveTownhallAdminDetail(chantId, {
                deity_image: deityImage || undefined,
                description: description || undefined,
                slides,
              });
              setMessage("Townhall content saved.");
            } catch (e) {
              setMessage(e?.message || "Save failed. Check slokas JSON format.");
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? "Saving..." : "Save Townhall content"}
        </Button>
        {message ? <p className="text-xs text-muted-foreground">{message}</p> : null}
      </CardContent>
    </Card>
  );
}

